/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import {Injectable} from '@angular/core';
import {Router} from "@angular/router";
import {AuthenticationService} from './authentication.service';
import {BaseService} from "./base-service";

import {PropertyPatch} from "../models/property-patch";
import {Zone} from '../models/zone';
import {ZoneInstance} from '../models/zone-instance';
import { PagedResults } from 'app/models/paged-results';
import { RestService } from './rest-service';
import { Url_Base } from 'app/rest-constants';


@Injectable()
export class ZoneService extends BaseService {


  constructor(
    private router: Router,
    private authenticationService: AuthenticationService,
    private mRestService: RestService
  ) {
    super(router, authenticationService);
  }

  getUrl(): string {
    return `${Url_Base}/locations/readzones/`;
  }


  getNumberZones(): Promise<Number> {
    const callUrl = this.getUrl() + 'count';
    return this.mRestService.get<Number>(callUrl);
  }

  getNumberZoneInstances(): Promise<Number> {
    const callUrl = this.getUrl() + 'instances/count';
    return this.mRestService.get<Number>(callUrl);
  }

  getAllZonesForPaging(start: number, size: number, order: String, filterString: String): Promise<PagedResults<Zone>> {
    const callUrl = this.getUrl() + 'paging?start=' + start + "&size=" + size + "&order=ASC&filterColumn=name&filterString=" + filterString;

    return this.mRestService.get<PagedResults<Zone>>(callUrl);
  }

  getAllZoneInstancesForPaging(start: number, size: number, order: String, filterString: String): Promise<PagedResults<ZoneInstance>> {
    const callUrl = this.getUrl() + 'instances/paging?start=' + start + "&size=" + size + "&order=ASC&filterColumn=name&filterString=" + filterString;

    return this.mRestService.get<PagedResults<ZoneInstance>>(callUrl);
  }

  getAllZones(): Promise<Zone[]> {
    const rootUrl = this.getUrl();
    return this.mRestService.get<Zone[]>(rootUrl);
  }

  getZoneById(id: number): Promise<Zone> {
    const callUrl = this.getUrl() + `${id}`;
    return this.mRestService.get<Zone>(callUrl);
  }

  getZonesByLocationId(id: number): Promise<ZoneInstance[]> {
    const rootUrl = `${Url_Base}/locations/instances/${id}/readzones/instances`;
    return this.mRestService.get<ZoneInstance[]>(rootUrl);
  }

  createZone(zone: Zone): Promise<Zone> {
    const callUrl = this.getUrl();
    return this.mRestService.post<Zone>(callUrl, zone);
  }

  updateZone(zone: Zone): Promise<Zone> {
    const callUrl = this.getUrl();
    return this.mRestService.put<Zone>(callUrl, zone);
  }

  deleteZones(ids: number[]): Promise<any> {
    const callUrl = this.getUrl();
    return this.mRestService.delete(callUrl, ids)
      .then(response => {return null;});
  }





  getAllZoneInstances(): Promise<ZoneInstance[]> {
    const rootUrl = this.getUrl() + 'instances';
    return this.mRestService.get<ZoneInstance[]>(rootUrl);
  }

  getZoneInstancesFiltered(start: number, size: number, order: string, filterString: string): Promise<ZoneInstance[]> {
    const url = this.getUrl() + `instances/filtered?start=` + start + "&size=" + size + "&order=" + order + "&filterColumn=name&filterString=" + filterString;
    return this.mRestService.get<ZoneInstance[]>(url);
  } 

  getZoneInstanceById(id: number): Promise<ZoneInstance> {
    const callUrl = this.getUrl() + `instances/${id}`;
    return this.mRestService.get<ZoneInstance>(callUrl);
  }

  createZoneInstance(zoneInstance: ZoneInstance): Promise<ZoneInstance> {
    const callUrl = this.getUrl() + 'instances';
    return this.mRestService.post<ZoneInstance>(callUrl, zoneInstance);
  }

  updateZoneInstance(zoneInstance: ZoneInstance): Promise<ZoneInstance> {
    const callUrl = this.getUrl() + 'instances';
    return this.mRestService.put<ZoneInstance>(callUrl, zoneInstance);
  }

  deleteZoneInstances(ids: number[]): Promise<any> {
    const callUrl = this.getUrl() + `instances`;
    return this.mRestService.delete(callUrl, ids);
  }

  executeFunction(zoneInstanceId: number, functionId: number, propertyPatches: PropertyPatch[], responseType?: string): Promise<any> {
    const callUrl = this.getUrl() + `instances/${zoneInstanceId}/execute-function?function_id=${functionId}`;
    return this.mRestService.post(callUrl, propertyPatches, responseType);
  }

}
